// 処理中の合図音 (spec 2026-07-27 §3.1)。AI が考えている間、画面を見ていない人に
// 「システムが働いている」を音だけで伝える。WebAudio 合成で音声ファイルを同梱せず
// vendor にも依存しない (testTone.ts と同じ方式、原則 4)。
//
// 値はすべて参考実装 (masuidrive-name-picker の earcon.js / earcon.py) の則に由来する:
//   - 0.5 秒より早く終わる処理では鳴らさない (全部に鳴らすと騒がしい)
//   - 連続音にせず間隔を空ける (連続だとエラー音に聞こえる)
//   - 繰り返し鳴るものは他の音の 1/5 の振幅にする (同じ音量だと耳につく)
//   - 端点を 0 にする (矩形に切るとプチッと鳴る)
// 2026-07-25 に全廃したターン合図音より低く・短く・小さいのは、全廃の理由だった
// 「音声が流れている時に鳴る SE で耳が痛い」の再発を避けるため (spec §1.2)。
//
// voice-first (原則 5): 鳴らせない環境でも例外を投げない。音が出なくても本編は進む。

const A4 = 440;
// A4 * 2^(-2/12) ≒ 391.995Hz。前回耳に付いた 587〜880Hz より下に置く。
const CUE_FREQ_HZ = A4 * 2 ** (-2 / 12);
const CUE_DURATION_MS = 55;
// 他の SE の 1/5。繰り返し鳴るので同じ音量だと耳につく。
const CUE_AMPLITUDE = 0.05;
const ATTACK_SEC = 0.004;
// 立ち下がりは指数減衰。exponentialRamp は 0 を取れないので十分小さい値に落とす。
const SILENT_AMPLITUDE = 0.0001;
// 発音後にノードを開放するまでの余韻。
const RELEASE_SEC = 0.02;
const START_DELAY_MS = 500;
const REPEAT_INTERVAL_MS = 850;

// module 単位の singleton。本編の processing と事前チェックの resolving は画面が別で
// 同時に起きないため、複数インスタンスを持つ必要がない (spec §3.1)。
//
// AudioContext は 1 つを持ち回す: 旧 earcons.ts は 1 回鳴らすたびに new して close して
// いたが、850ms ごとに繰り返す用途では生成・破棄を繰り返すことになる。初回 start() まで
// 生成を遅延し、以後は保持する (sinkId は扱わない — この音は「鳴れば十分」、YAGNI)。
let ctx: AudioContext | null = null;
let delayTimer: ReturnType<typeof setTimeout> | null = null;
let repeatTimer: ReturnType<typeof setInterval> | null = null;

function ensureContext(): AudioContext | null {
  if (ctx) return ctx;
  try {
    ctx = new AudioContext();
  } catch {
    // WebAudio 非対応 / test / SSR。黙って何もしない (原則 5)
    return null;
  }
  return ctx;
}

function playTone(): void {
  const audio = ctx;
  if (!audio) return;
  try {
    const osc = audio.createOscillator();
    const gain = audio.createGain();
    osc.frequency.value = CUE_FREQ_HZ;
    const t = audio.currentTime;
    // 立ち上がりに数 ms かけ、指数で減衰させる。切り立てるとプチッと鳴る。
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(CUE_AMPLITUDE, t + ATTACK_SEC);
    gain.gain.exponentialRampToValueAtTime(SILENT_AMPLITUDE, t + CUE_DURATION_MS / 1000);
    osc.connect(gain);
    gain.connect(audio.destination);
    osc.start(t);
    osc.stop(t + CUE_DURATION_MS / 1000 + RELEASE_SEC);
  } catch {
    // 繰り返しタイマーの中から呼ばれるので、ここで throw させると未捕捉例外になる。
    // 鳴らないだけで処理は続ける (原則 5)。
  }
}

// 鳴らし始める。500ms 以内に stop() されたら 1 度も鳴らない。
export function start(): void {
  // 二重呼び出しで 2 本目のタイマーを張らない (張ると stop() 後も片方が鳴り続ける)。
  if (delayTimer !== null || repeatTimer !== null) return;
  const audio = ensureContext();
  if (!audio) return;
  // autoplay policy で suspended のことがある (事前チェックのマイク許可でユーザー操作を
  // 経ているため実際には通る)。失敗しても無音のまま続行する。
  if (audio.state === "suspended") {
    void audio.resume().catch(() => undefined);
  }
  delayTimer = setTimeout(() => {
    delayTimer = null;
    playTone();
    repeatTimer = setInterval(playTone, REPEAT_INTERVAL_MS);
  }, START_DELAY_MS);
}

// 鳴りやめる。start() していない状態で呼んでも no-op。
// 遅延タイマーと繰り返しタイマーの両方を clear する (片方だけ残ると鳴り続ける)。
export function stop(): void {
  if (delayTimer !== null) {
    clearTimeout(delayTimer);
    delayTimer = null;
  }
  if (repeatTimer !== null) {
    clearInterval(repeatTimer);
    repeatTimer = null;
  }
}
